// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { columnChart, mixSparkHtml } from '../components/charts';
import { FUEL_COLOUR, FUEL_ORDER } from '../fuels';
import type { Postcode } from '../types';
import { histogram, MIN_VEHICLES, pcEvShare, pcShare } from '../analysis';
import { STATE_ORDER } from '../data';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';

type SortKey = 'pc' | 'total' | 'bev' | 'ev' | 'diesel' | 'petrol';

export function renderExplorer(root: HTMLElement, ctx: ViewContext): void {
  const all = ctx.data.postcodes;
  let query = '';
  let state = 'ALL';
  let sortKey: SortKey = 'total';
  let sortDir: 1 | -1 = -1;
  let binFilter: { lo: number; hi: number } | null = null;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Explore every postcode</h1>
      <p class="view-sub">All ${formatNumber(all.length)} postcodes with registered passenger cars. Search, sort, or click a
        distribution bar to filter. Click any row to open it.</p>
    </div>

    <div class="panel" style="margin-bottom:var(--space-lg)">
      <div class="panel-title">Distribution — electric share across postcodes</div>
      <div class="panel-sub">How many postcodes fall in each electric-share band (500+ cars). Click a bar to filter the table; click again to clear.</div>
      <div class="chart-scroll" data-role="hist"></div>
    </div>

    <div class="controls">
      <input class="text-input" type="search" placeholder="Search postcode or suburb…" aria-label="Search" data-role="q" style="width:min(260px,60vw)" />
      <span class="field-label">State</span>
      <select data-role="state">
        <option value="ALL">All</option>
        ${STATE_ORDER.map((s) => `<option value="${s}">${s}</option>`).join('')}
      </select>
      <span class="field-label" data-role="count"></span>
      <span data-role="binchip"></span>
    </div>

    <div class="table-scroll">
      <table class="data">
        <thead><tr>
          <th class="sortable" data-sort="pc">Postcode</th>
          <th>Locality</th>
          <th>State</th>
          <th class="sortable t-right" data-sort="total">Cars</th>
          <th class="sortable t-right" data-sort="bev">Electric</th>
          <th class="sortable t-right" data-sort="ev">Electrified</th>
          <th class="sortable t-right" data-sort="diesel">Diesel</th>
          <th>Fuel mix</th>
        </tr></thead>
        <tbody data-role="rows"></tbody>
      </table>
    </div>
    <p class="empty" data-role="empty" style="display:none">No postcodes match.</p>
  `;

  const valOf = (p: Postcode, k: SortKey): number | string => {
    switch (k) {
      case 'pc':
        return p.pc;
      case 'total':
        return p.total;
      case 'bev':
        return pcShare(p, 'bev') ?? -1;
      case 'ev':
        return pcEvShare(p) ?? -1;
      case 'diesel':
        return pcShare(p, 'diesel') ?? -1;
      case 'petrol':
        return pcShare(p, 'petrol') ?? -1;
    }
  };

  const rowsEl = root.querySelector('[data-role="rows"]') as HTMLElement;
  const countEl = root.querySelector('[data-role="count"]') as HTMLElement;
  const emptyEl = root.querySelector('[data-role="empty"]') as HTMLElement;
  const binChip = root.querySelector('[data-role="binchip"]') as HTMLElement;

  const filtered = (): Postcode[] => {
    const q = query.trim().toLowerCase();
    let rows = all.filter((p) => {
      if (state !== 'ALL' && p.st !== state) return false;
      if (q && !(p.pc.startsWith(q) || p.locs.some((l) => l.toLowerCase().includes(q)))) return false;
      if (binFilter) {
        const ev = pcShare(p, 'bev');
        if (ev == null || p.total < MIN_VEHICLES || ev < binFilter.lo || ev >= binFilter.hi) return false;
      }
      return true;
    });
    rows = rows.slice().sort((a, b) => {
      const av = valOf(a, sortKey);
      const bv = valOf(b, sortKey);
      if (typeof av === 'string' || typeof bv === 'string') return String(av).localeCompare(String(bv)) * sortDir;
      return (av - bv) * sortDir;
    });
    return rows;
  };

  const paint = () => {
    const rows = filtered().slice(0, 400);
    const totalMatch = filtered().length;
    countEl.textContent = `${formatNumber(totalMatch)} postcode${totalMatch === 1 ? '' : 's'}${totalMatch > 400 ? ' (showing top 400)' : ''}`;
    binChip.innerHTML = binFilter ? `<button class="chip" data-role="clearbin">electric ${binFilter.lo}–${binFilter.hi}% ✕</button>` : '';
    emptyEl.style.display = rows.length ? 'none' : 'block';
    rowsEl.innerHTML = rows
      .map((p) => {
        return `<tr data-pc="${p.pc}">
          <td class="pc-cell">${escapeHtml(p.pc)}</td>
          <td class="loc-cell">${escapeHtml(p.loc || '—')}</td>
          <td>${escapeHtml(p.st)}</td>
          <td class="t-num">${formatNumber(p.total)}</td>
          <td class="t-num" style="color:${FUEL_COLOUR.bev}">${formatPercent(pcShare(p, 'bev'))}</td>
          <td class="t-num" style="color:${FUEL_COLOUR.hybrid}">${formatPercent(pcEvShare(p))}</td>
          <td class="t-num" style="color:${FUEL_COLOUR.diesel}">${formatPercent(pcShare(p, 'diesel'))}</td>
          <td>${mixSparkHtml(p, FUEL_ORDER)}</td>
        </tr>`;
      })
      .join('');
    root.querySelectorAll('th.sortable').forEach((th) => {
      const k = th.getAttribute('data-sort');
      th.innerHTML = th.innerHTML.replace(/\s*[▲▼]$/, '') + (k === sortKey ? (sortDir === -1 ? ' <span class="arrow">▼</span>' : ' <span class="arrow">▲</span>') : '');
    });
  };

  // Histogram.
  const histSlot = root.querySelector('[data-role="hist"]') as HTMLElement;
  const rankableRows = all.filter((p) => p.total >= MIN_VEHICLES);
  const bins = histogram(rankableRows, (p) => pcShare(p, 'bev'), 12, 6);
  histSlot.appendChild(
    columnChart(
      bins.map((b, i) => ({
        id: String(i),
        label: `${b.from.toFixed(1)}`,
        value: b.count,
        colour: FUEL_COLOUR.bev,
        tip: `${b.from.toFixed(1)}–${b.to.toFixed(1)}% electric: ${formatNumber(b.count)} postcodes`,
      })),
      {
        width: 900,
        height: 220,
        labelEvery: 2,
        valueFormat: (v) => formatNumber(v),
        onClick: (id) => {
          const b = bins[Number(id)];
          binFilter = binFilter && binFilter.lo === b.from ? null : { lo: b.from, hi: b.to };
          paint();
        },
      },
    ),
  );

  // Wire controls.
  const qEl = root.querySelector('[data-role="q"]') as HTMLInputElement;
  let timer: number | undefined;
  qEl.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      query = qEl.value;
      paint();
    }, 300);
  });
  (root.querySelector('[data-role="state"]') as HTMLSelectElement).addEventListener('change', (e) => {
    state = (e.target as HTMLSelectElement).value;
    paint();
  });
  root.querySelectorAll('th.sortable').forEach((th) =>
    th.addEventListener('click', () => {
      const k = th.getAttribute('data-sort') as SortKey;
      if (k === sortKey) sortDir = (sortDir * -1) as 1 | -1;
      else {
        sortKey = k;
        sortDir = k === 'pc' ? 1 : -1;
      }
      paint();
    }),
  );
  rowsEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-pc]');
    if (tr) ctx.openPostcode(tr.getAttribute('data-pc') as string);
  });
  root.addEventListener('click', (e) => {
    if ((e.target as Element).closest('[data-role="clearbin"]')) {
      binFilter = null;
      paint();
    }
  });

  paint();
}
